import { Button, Chip, Stack, styled, SxProps } from "@mui/material";
import React from "react";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import moment from "moment";
import { useDispatch } from "react-redux";
import { cacheService } from "../../../Core/Api/services";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import { MetadataFieldValueObjectTypes } from "../../../Core/Api/Types/metadata-field-value-object-types";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import {
  GetAssetIdsFilter,
  GetAssetStatusFilter,
  GetClassFilter,
  GetConfidentialityFilter,
  GetCreatedbyFilter,
  GetFilters,
  GetHideWithoutAssetFilter,
  GetProviderIdFilter,
  GetStatusHistoryFilter,
  GetTitleCustomerIdsFilter,
  GetTypeFilter,
  RemoveFilterById,
  RemoveStatusHistoryFilter,
  SetAssetIdsFilter,
  SetAssetStatusFilter,
  SetClassFilter,
  SetCreatedbyFilter,
  SetProviderIdFilter,
  SetTitleCustomerIdsFilter,
  SetTypeFilter,
} from "../../../Redux/Reducers/redux-slice-filters";
import { useFiltersPanel } from "../../../Utils/Hooks/useFiltersPanel";
import { GetObjectStatusLabel } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../Redux/redux-store";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
export interface FiltersListProps {
  sx?: SxProps;
  layer: ObjectLayer | null;
  type: MetadataFieldValueObjectTypes;
  metadataFieldsOverride?: DbMetadataFieldInfos[];
  disabled?: boolean;
  isDrive?: boolean;
}

const FiltersList: React.FC<FiltersListProps> = (props) => {
  const { t } = useTranslation();
  const { sx, layer, type, metadataFieldsOverride, disabled = false, isDrive = false } = props;
  const { openFiltersPanel } = useFiltersPanel();

  const metadataFields = React.useMemo(
    () => (!metadataFieldsOverride ? cacheService.GetMetadata() : metadataFieldsOverride),
    [metadataFieldsOverride]
  );
  let customerName = cacheService.GetCustomerExternalIdLabel();

  const handleAddFiltersClick: React.MouseEventHandler = () => {
    openFiltersPanel(type, layer, metadataFieldsOverride);
  };

  // Redux
  const dispatch = useDispatch();
  const typeFilter = useAppSelector(GetTypeFilter);
  const classFilter = useAppSelector(GetClassFilter);
  const statusFilter = useAppSelector(GetAssetStatusFilter);
  const titleCustomerIdsFilter = useAppSelector(GetTitleCustomerIdsFilter);
  const assetIdsFilter = useAppSelector(GetAssetIdsFilter);
  const hideWithoutAssetFilter = useAppSelector(GetHideWithoutAssetFilter);
  const confidentialityFilter = useAppSelector(GetConfidentialityFilter);
  const createdbyFilter = useAppSelector(GetCreatedbyFilter);
  const providerIdFilter = useAppSelector(GetProviderIdFilter);
  const statusHistoryFilter = useAppSelector(GetStatusHistoryFilter);
  const filters = useAppSelector(GetFilters);

  let dateFilters = [
    ...new Set(
      filters.filter((x) => x.fieldType === BaseMetadataType.DbDateTimeMetadataValueInfos).map((item) => item.id)
    ),
  ];

  const isAdam = cacheService.GetPlatformMode() === PlatformMode.Adam;

  // Funcs
  const getMetaLabel = (id: number) => {
    return metadataFields.find((m) => m.id === id)?.label;
  };

  const getValue = (filter: IDbBaseMetadataValueInfos): string | null => {
    switch (filter.fieldType) {
      case BaseMetadataType.DbTerritoryCodeMetadataValueInfos:
        return filter.value.label + " (" + filter.value.value + ")";
      case BaseMetadataType.DbStringMetadataValueInfos:
      case BaseMetadataType.DbLongMetadataValueInfos:
      case BaseMetadataType.DbDoubleMetadataValueInfos:
        return filter.value;
      case BaseMetadataType.DbTitlesMetadataValueInfos:
      case BaseMetadataType.DbSynopsesMetadataValueInfos:
        return filter.value.map((val: { text: any }) => val.text);
      case BaseMetadataType.DbCastsMetadataValueInfos:
        return filter.value?.map((v) => v.lastname).join(", ");
      case BaseMetadataType.DbStringMultiMetadataValueInfos:
      case BaseMetadataType.DbLongMultiMetadataValueInfos:
        return (
          metadataFieldsOverride
            ?.find((x) => x.id === filter.id)
            ?.values?.filter((x) => filter.value.includes(x.value))
            .map((x) => x.label)
            .join(", ") ?? filter.value?.map((v) => v).join(", ")
        );
      case BaseMetadataType.DbLanguageCodeMetadataValueInfos:
        return filter.value.label + " (" + filter.value.value + ")";
      case BaseMetadataType.DbLanguageCodeMultiMetadataValueInfos:
        return filter.value.map((val: { value: any }) => val.value);
      case BaseMetadataType.DbBoolMetadataValueInfos:
        return filter.value ? "Yes" : "No";
      case BaseMetadataType.DbDateTimeMetadataValueInfos:
        return moment(filter.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS);
    }

    return null;
  };

  return (
    <Stack spacing={0.5} direction="row" alignItems="center" sx={{ ...sx, flexWrap: "wrap", gap: 0.5 }}>
      <Button
        variant="outlined"
        size="small"
        sx={{ mr: 0.5 }}
        startIcon={<AddRoundedIcon />}
        onClick={handleAddFiltersClick}
        disabled={false}
      >
        {t("button:addFilters")}
      </Button>

      {hideWithoutAssetFilter && <FilterChip variant="tag" label={t("chip:label.hideWithoutAssets")} size="medium" />}

      {confidentialityFilter && <FilterChip variant="tag" label={t("common:confidentiality")} size="medium" />}

      {createdbyFilter && (
        <FilterChip
          variant="tag"
          label={t("chip:label.createdByUser", { user: createdbyFilter.labels.join(", ") })}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetCreatedbyFilter(null))}
        />
      )}
      {providerIdFilter && providerIdFilter?.ids.length > 0 && (
        <FilterChip
          variant="tag"
          label={t("chip:label.providedByProvider", { provider: providerIdFilter.labels.join(", ") })}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetProviderIdFilter(null))}
        />
      )}

      {classFilter && classFilter?.length > 0 && !isDrive && (
        <FilterChip
          variant="tag"
          label={t("chip:label.classFilter", { filter: classFilter })}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetClassFilter([]))}
        />
      )}

      {statusFilter && statusFilter?.length > 0 && (
        <FilterChip
          variant="tag"
          label={t("chip:label.statusFilter", { filter: statusFilter })}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetAssetStatusFilter([]))}
        />
      )}

      {typeFilter && typeFilter?.length > 0 && (
        <FilterChip
          variant="tag"
          label={t("chip:label.typeFilter", { filter: typeFilter })}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetTypeFilter([]))}
        />
      )}

      {assetIdsFilter && assetIdsFilter?.length > 0 && (
        <FilterChip
          variant="tag"
          label={t("chip:label.filteringOnAssetIds", { count: assetIdsFilter.length })}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetAssetIdsFilter([]))}
        />
      )}
      {customerName && titleCustomerIdsFilter && titleCustomerIdsFilter?.length > 0 && (
        <FilterChip
          variant="tag"
          label={customerName + ": " + titleCustomerIdsFilter.join(", ")}
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(SetTitleCustomerIdsFilter([]))}
        />
      )}

      {dateFilters.map((date, index) => (
        <FilterChip
          variant="tag"
          key={index}
          label={
            getMetaLabel(date) +
            ": " +
            (filters.filter((y) => y.id === date).length > 1
              ? filters
                  .filter((y) => y.id === date)
                  .map((date) => getValue(date))
                  .join(" - ")
              : "> " + getValue(filters.find((y) => y.id === date)!))
          }
          size="medium"
          deleteIcon={<CloseRoundedIcon />}
          onDelete={() => dispatch(RemoveFilterById(date))}
        />
      ))}

      {statusHistoryFilter.map((x, index) => (
        <FilterChip
          variant="tag"
          key={index}
          size="medium"
          label={
            GetObjectStatusLabel(x.status, isAdam) +
            ": " +
            (x.startDate
              ? x.endDate
                ? `${moment(x.startDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)} - ${moment(
                    x.endDate
                  ).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}`
                : `> ${moment(x.startDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}`
              : x.endDate
              ? `< ${moment(x.endDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}`
              : t("common:exist"))
          }
          onDelete={() => dispatch(RemoveStatusHistoryFilter(x.status))}
          deleteIcon={<CloseRoundedIcon />}
        />
      ))}

      {filters
        .filter((x) => x.fieldType !== BaseMetadataType.DbDateTimeMetadataValueInfos)
        .filter((x) => !x.hideFilter)
        .map((filter, index) => (
          <FilterChip
            variant="tag"
            key={index}
            label={
              (filter.fieldType === BaseMetadataType.DbDateTimeMetadataValueInfos
                ? getMetaLabel(filter.id) +
                  " (" +
                  (filter.valueLabel === "startDate" ? t("common:lowerCaseStartDate") : t("common:lowerCaseEndDate")) +
                  ")"
                : getMetaLabel(filter.id)) +
              ": " +
              getValue(filter)
            }
            size="medium"
            deleteIcon={<CloseRoundedIcon />}
            onDelete={() => dispatch(RemoveFilterById(filter.id))}
          />
        ))}
    </Stack>
  );
};

const FilterChip = styled(Chip)(({ theme }) => ({
  height: "auto",
  minHeight: "32px",
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  marginLeft: "0 !important",
  ".MuiChip-label": {
    whiteSpace: "wrap",
  },
}));

export default FiltersList;
